import {
  computed,
  defineComponent,
  onMounted,
  ref,
  shallowRef,
  watch,
} from 'vue'
import {
  at,
  filter,
  get,
  isEmpty,
  join,
  mergeWith,
  remove,
} from 'lodash-unified'
import { createNamespace } from '@xzx-design/utils'
import { Pic } from '@xzx-design/icons-vue'
import {
  Cell,
  Empty,
  Icon,
  Image,
  List,
  Loading,
  PullRefresh,
  showToast,
} from '../../'

import type { BaseListOptions } from './types'

const [name, bem, t] = createNamespace('base-list')

export default defineComponent({
  name,
  props: {
    options: {
      type: Object,
      default: () => ({}),
    },
    safeAreaInsetBottom: {
      type: Boolean,
      default: false,
    },
    center: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    cutline: {
      type: Boolean,
      default: true,
    },
    isLink: {
      type: Boolean,
      default: false,
    },
    immediateCheck: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['item-click', 'long-press', 'finished', 'error'],
  setup(props, { emit, slots, expose }) {
    /**
     * 基本options
     */
    const defaultOptions = computed<BaseListOptions>(() => ({
      usePagination: true, // 使用分页加载
      usePullRefresh: false, // 使用下拉刷新
      usePadding: true, // 使用边距
      useBackground: true, // 使用背景色
      useCell: false, // 使用cell组件
      cellValueWidthAuto: false,
      useIcon: true,
      iconSize: 30,
      useManuallyLoad: false, // 使用手动加载
      autoInitRequest: true, // 自动初始化请求
      cellPath: {
        // cell值的取值path
        title: 'title',
        label: 'label',
        value: 'value',
        icon: 'icon',
      },
      pagination: {
        current: 1,
        size: 10,
        pages: 0,
      },
      paginationKeys: {
        current: 'current',
        size: 'size',
        pages: 'pages',
        total: 'total',
      },
      params: {}, // 请求参数
      request: null, // 接口请求
      data: [],
      resultPath: {
        // 接口结果的取值path
        data: 'data',
        pages: 'data.pages',
        total: 'data.total',
      },
      errorIcon: '',
      style: {
        //
      },
      filter: {
        includes: [],
        keyword: '',
      },
    }))

    /**
     * 与传入的options合并
     */
    const mergedOptions = computed(() => {
      let obj: any = {}
      obj = mergeWith({}, defaultOptions.value, props.options)
      if (
        typeof props.options?.useIcon === 'boolean' &&
        !props.options?.useIcon
      ) {
        delete obj.cellPath.icon
      }
      return obj
    })

    let sourceData: any = []
    const dataList = ref([])
    const loading = ref(false)
    const loadingList = ref(false)
    const isRefreshing = ref(false)
    const finished = ref(false)
    const longPressTimer = ref()
    const pagination = ref({
      current: mergedOptions.value?.pagination?.current,
      size: mergedOptions.value?.pagination?.size,
      pages: 0,
    })
    /**
     * 获取列表数据
     * @param {*} init
     */
    async function getList(init = true) {
      if (init) {
        pagination.value.current = mergedOptions.value?.pagination?.current
        finished.value = false
      }
      if (finished.value) return // 防止多一次请求
      const params = {
        ...mergedOptions.value?.params,
      }
      // 如果使用分页
      if (mergedOptions.value?.usePagination) {
        params[mergedOptions.value?.paginationKeys.current] =
          pagination.value.current
        params[mergedOptions.value?.paginationKeys.size] = pagination.value.size
      }
      loadingList.value = true
      await mergedOptions.value
        ?.request(params)
        .then((r: any) => {
          if (r.code === 200) {
            if (mergedOptions.value?.usePagination) {
              const pages =
                get(r, mergedOptions.value?.resultPath.pages) ||
                roundUpToInteger(
                  get(r, mergedOptions.value?.resultPath.total) /
                    pagination.value.size
                )
              if (r.data && pagination.value.current <= pages) {
                const newData = get(r, mergedOptions.value?.resultPath.data)
                sourceData = dataList.value = init
                  ? newData
                  : [...dataList.value, ...newData]
                if (pagination.value.current === pages) {
                  finished.value = true
                  loadingList.value = false
                  emit('finished', true, dataList.value)
                } else {
                  pagination.value.current++
                }
              } else {
                finished.value = true
                loadingList.value = false
                if (
                  pagination.value.current ===
                  mergedOptions.value?.pagination?.current
                )
                  sourceData = dataList.value = []
                emit('finished', true, dataList.value)
              }
            } else {
              sourceData = dataList.value = get(
                r,
                mergedOptions.value?.resultPath.data
              )
              finished.value = true
              emit('finished', true, dataList.value)
            }
          } else {
            showToast(r.msg)
            emit('error', r)
            finished.value = true
          }
        })
        .catch((err: any) => {
          finished.value = true
          isRefreshing.value = loadingList.value = false
          emit('error', err)
        })
      loading.value = loadingList.value = false
    }

    /**
     *
     * @param {*} decimalNumber
     * @example 0.1 -> 1
     * @example 10.1 -> 11
     * @example 10 -> 10
     *
     */
    function roundUpToInteger(decimalNumber: number) {
      // 检查是否是整数
      if (Number.isInteger(decimalNumber)) {
        return decimalNumber
      }
      // 取整数部分并加一
      const integerPart = Math.floor(decimalNumber)
      const result = integerPart + 1
      return result
    }

    /**
     * 点击一项时
     * @param {*} item
     */
    function onClickItem(item: any) {
      if (props.disabled || item?.disabled) return
      emit('item-click', item)
    }

    /**
     * 触底加载
     */
    function onLoad() {
      if (!mergedOptions.value?.usePagination) return
      loadingList.value = true
      getList(false)
    }

    /**
     * 刷新页面
     */
    async function refresh() {
      isRefreshing.value = true
      await getList(true)
      isRefreshing.value = false
    }

    /**
     * 静态删除
     * @param {*} ids
     */
    function deletes(ids: any[], key = 'id') {
      sourceData = dataList.value = remove(
        dataList.value,
        (item) => !ids?.includes(item[key])
      )
    }

    /**
     * 手动加载分页
     * @param {*} ids
     */
    async function paginationLoad() {
      if (finished.value) return
      await getList(false)
      loadingList.value = false
    }

    /**
     * 长按
     * @param {*} item
     */
    function longPress(type: string, item: any) {
      if (type === 'start') {
        longPressTimer.value = setTimeout(() => {
          emit('long-press', item)
        }, 500)
      }

      if (type === 'end') {
        clearTimeout(longPressTimer.value)
      }
    }

    /**
     * props取值
     * @param {*} params
     */
    function cellProps(item: any) {
      const cellPath = mergedOptions.value.cellPath
      // 使用简化的函数来处理路径数据
      const getPathData = (key: string) =>
        typeof cellPath[key] === 'function'
          ? cellPath[key](item)
          : get(item, cellPath[key])
      // 构建 pathData 对象
      return {
        title: getPathData('title'),
        label: getPathData('label'),
        value: getPathData('value'),
        icon: getPathData('icon'),
      }
    }

    async function loadingRefresh(init: boolean) {
      loading.value = true
      await getList(init)
      loading.value = false
    }

    /**
     * 监听数据，兼容异步传入
     */
    watch(
      () => mergedOptions.value.data,
      (val) => {
        if (!mergedOptions.value.request) {
          sourceData = dataList.value = val
        }
      },
      {
        deep: true,
        immediate: true,
      }
    )

    watch(
      () => mergedOptions.value.filter,
      (val) => {
        dataList.value = filter(sourceData, (v) => {
          const includesStr = join(at(v, val.includes), '')
          if (includesStr?.includes(val?.keyword)) {
            return v
          }
        }) as never[]
      },
      {
        deep: true,
        immediate: true,
      }
    )

    onMounted(async () => {
      // alert(1)
      if (mergedOptions.value?.request) {
        if (mergedOptions.value?.autoInitRequest) {
          loadingRefresh(true)
        }
      } else {
        finished.value = true
        loadingList.value = false
      }
    })

    expose({
      getList,
      refresh,
      deletes,
      paginationLoad,
      loadingRefresh,
      data: dataList,
    })

    const rootClass = computed(() => [
      bem(),
      props.safeAreaInsetBottom && 'xzx-safe-area-bottom',
      mergedOptions.value?.usePadding &&
        bem({
          padding: true,
        }),
      !props.cutline && bem({ 'no-cutline': true }),
      mergedOptions.value?.useBackground && bem({ background: true }),
      mergedOptions.value?.cellValueWidthAuto &&
        bem({ 'cell-value-width-auto': true }),
    ])

    const renderList = () => {
      const listComponent = ref(
        mergedOptions.value?.request && mergedOptions.value?.usePagination
          ? List
          : 'div'
      )
      return (
        <listComponent.value
          v-model={loadingList.value}
          finished={finished.value}
          loading={loadingList.value}
          onLoad={
            !mergedOptions.value?.useManuallyLoad &&
            mergedOptions.value?.request &&
            onLoad()
          }
          offset="30"
          immediate-check={props.immediateCheck}
          v-slots={{
            finished: () => {
              return <span>{t('noMore')}</span>
            },
          }}
        >
          {dataList.value.map((item, index) => renderListItem(item, index))}
        </listComponent.value>
      )
    }

    const renderIcon = (item: any) => {
      const iconInstance = computed(() => cellProps(item)?.icon)
      const isXzxIcon = typeof iconInstance.value === 'object'
      return isXzxIcon ? (
        <Icon size={mergedOptions.value.iconSize}>
          <iconInstance.value />
        </Icon>
      ) : (
        <Image
          src={iconInstance.value}
          fit="cover"
          width={mergedOptions.value.iconSize}
          height={mergedOptions.value.iconSize}
          v-slots={{
            error: () => (
              <Icon>
                <Pic />
              </Icon>
            ),
          }}
        />
      )
    }

    const renderListItem = (item: any, index: number) => {
      const listItemComponent = shallowRef(
        mergedOptions.value?.useCell ? Cell : 'div'
      )
      const listItemClass = computed(() => [
        (props.disabled || item?.disabled) &&
          bem('item', {
            disabled: true,
          }),
        index === 0 &&
          bem('item', {
            first: true,
          }),
        index === dataList.value?.length - 1 &&
          bem('item', {
            last: true,
          }),
      ])
      return (
        <listItemComponent.value
          class={listItemClass.value}
          onClick={() => onClickItem(item)}
          onTouchstart={() => longPress('start', item)}
          onTouchend={() => longPress('end', item)}
          isLink={props?.isLink || item?.isLink}
          {...(mergedOptions.value?.useCell ? cellProps(item) : {})}
          center={props.center}
          v-slots={{
            default:
              slots?.default && !mergedOptions.value.useCell
                ? () =>
                    slots.default!({
                      item,
                      scopeIndex: index,
                      dataLength: dataList.value?.length,
                    })
                : null,
            icon: mergedOptions.value?.useIcon
              ? () => {
                  return (
                    <div class={bem('cell-icon')}>
                      {slots?.icon
                        ? slots.icon({ item, scopeIndex: index })
                        : renderIcon(item)}
                    </div>
                  )
                }
              : undefined,
            title: slots?.title
              ? () => slots.title!({ item, scopeIndex: index })
              : undefined,
            label: slots?.label
              ? () => slots.label!({ item, scopeIndex: index })
              : undefined,
            value: slots?.value
              ? () => slots.value!({ item, scopeIndex: index })
              : undefined,
            rightIcon: slots?.rightIcon
              ? () => slots.rightIcon!({ item, scopeIndex: index })
              : undefined,
          }}
        />
      )
    }

    const renderLoading = () => {
      return (
        <div class={bem('loading-box')}>
          {!slots?.['content-loading'] ? (
            <Loading
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            />
          ) : (
            slots['content-loading']({ dataLength: dataList.value?.length })
          )}
        </div>
      )
    }

    const renderEmpty = () => {
      return (
        <Empty
          description={mergedOptions.value?.emptyText}
          image={mergedOptions.value?.emptyImage}
        />
      )
    }

    const renderExtra = () => {
      return (
        <>
          {slots?.['content-extra'] &&
            slots?.['content-extra']({ dataLength: dataList.value?.length })}
        </>
      )
    }

    const rootComponent = computed(() =>
      mergedOptions.value?.usePullRefresh && mergedOptions.value?.request
        ? PullRefresh
        : 'div'
    )
    return () => (
      <rootComponent.value
        modelValue={isRefreshing}
        onUpdate:modelValue={(val: boolean) => (isRefreshing.value = val)}
        class={rootClass.value}
        onRefresh={refresh}
        style={mergedOptions.value?.style}
      >
        {loading.value
          ? renderLoading()
          : !isEmpty(dataList.value)
          ? renderList()
          : renderEmpty()}
        {renderExtra()}
      </rootComponent.value>
    )
  },
})
